import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
} from '@mui/material';
import { supabaseService } from '../services/supabaseService';

// Type definitions
interface PremiumSubscription {
  id: string;
  user_id: string;
  package_type: string;
  price: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  status: string;
  payment_method: string;
  admin_note?: string;
  created_at: string;
  user?: {
    id: string;
    name: string;
    email: string;
    photos?: string[];
  };
}

interface PremiumStats {
  total_premium: number;
  active_premium: number;
  cancelled_premium: number;
  expired_premium: number;
  monthly_revenue: number;
}

const PremiumSubscriptionsPage: React.FC = () => {
  const [premiumSubscriptions, setPremiumSubscriptions] = useState<PremiumSubscription[]>([]);
  const [stats, setStats] = useState<PremiumStats>({
    total_premium: 0,
    active_premium: 0,
    cancelled_premium: 0,
    expired_premium: 0,
    monthly_revenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [packageType, setPackageType] = useState('1_month');
  const [adminNote, setAdminNote] = useState('');

  // Load premium subscriptions data
  const loadPremiumData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Premium data loading started...');

      // Get all premium subscriptions
      const premiums = await supabaseService.getAllPremiumSubscriptions({ limit: 100 });
      console.log('📊 Premium subscriptions fetched:', premiums.length, premiums);
      
      setPremiumSubscriptions(premiums);

      // Calculate stats
      const totalPremium = premiums.length;
      const activePremium = premiums.filter(p => p.is_active && p.status === 'active').length;
      const cancelledPremium = premiums.filter(p => p.status === 'cancelled').length;
      const expiredPremium = premiums.filter(p => p.status === 'expired').length;
      
      // Calculate monthly revenue (active subscriptions)
      const getMonthlyPrice = (type: string, price: number): number => {
        switch (type) {
          case '1_month': return price;
          case '3_month': return price / 3;
          case '6_month': return price / 6;
          case '12_month': return price / 12;
          default: return price;
        }
      };

      const monthlyRevenue = premiums
        .filter(p => p.is_active && p.status === 'active')
        .reduce((sum, p) => {
          // Calculate monthly equivalent
          const monthly = getMonthlyPrice(p.package_type, p.price);
          return sum + monthly;
        }, 0);

      const statsData = {
        total_premium: totalPremium,
        active_premium: activePremium,
        cancelled_premium: cancelledPremium,
        expired_premium: expiredPremium,
        monthly_revenue: monthlyRevenue,
      };

      console.log('📈 Calculated stats:', statsData);
      setStats(statsData);

    } catch (err) {
      console.error('❌ Error loading premium data:', err);
      setError('Premium subscriptions yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPremiumData();
  }, [loadPremiumData]);

  // Helper function to get package display name
  const getPackageDisplayName = (packageType: string): string => {
    switch (packageType) {
      case '1_month': return '1 Aylık';
      case '3_month': return '3 Aylık';
      case '6_month': return '6 Aylık';
      case '12_month': return '12 Aylık';
      default: return packageType;
    }
  };

  // Helper function to get status color
  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'cancelled': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(amount);
  };

  // Handle creating new premium subscription
  const handleCreatePremium = async () => {
    if (!selectedUser || !packageType) {
      alert('Lütfen kullanıcı ve paket tipini seçin');
      return;
    }

    try {
      const packageData = {
        '1_month': { price: 89.99, days: 30 },
        '3_month': { price: 199.99, days: 90 },
        '6_month': { price: 399.99, days: 180 },
        '12_month': { price: 599.99, days: 365 },
      };

      const { price, days } = packageData[packageType as keyof typeof packageData];

      const success = await supabaseService.createPremiumSubscription({
        userId: selectedUser,
        packageType,
        price,
        durationDays: days,
        paymentMethod: 'admin_grant',
        adminNote: adminNote || 'Admin tarafından verildi',
      });

      if (success) {
        alert('Premium abonelik başarıyla oluşturuldu!');
        setCreateDialogOpen(false);
        setSelectedUser('');
        setPackageType('1_month');
        setAdminNote('');
        loadPremiumData();
      } else {
        alert('Premium abonelik oluşturulurken hata oluştu!');
      }
    } catch (error) {
      console.error('Create premium error:', error);
      alert('Premium abonelik oluşturulurken hata oluştu!');
    }
  };

  // Handle cancelling premium subscription
  const handleCancelPremium = async (premiumId: string, userName: string) => {
    if (window.confirm(`"${userName}" kullanıcısının premium aboneliğini iptal etmek istediğinizden emin misiniz?`)) {
      try {
        const success = await supabaseService.cancelPremiumSubscription(premiumId);
        if (success) {
          alert('Premium abonelik iptal edildi!');
          loadPremiumData();
        } else {
          alert('Premium abonelik iptal edilirken hata oluştu!');
        }
      } catch (error) {
        console.error('Cancel premium error:', error);
        alert('Premium abonelik iptal edilirken hata oluştu!');
      }
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Premium Subscriptions
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: 3, 
          mb: 3 
        }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent>
                <Skeleton height={60} />
                <Skeleton height={40} />
              </CardContent>
            </Card>
          ))}
        </Box>

        <Paper sx={{ p: 2 }}>
          <Skeleton height={400} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Premium Subscriptions
        </Typography>
        <Button
          variant="contained"
          onClick={() => setCreateDialogOpen(true)}
          sx={{ backgroundColor: '#1976d2' }}
        >
          Premium Ver
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: 3, 
        mb: 3 
      }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Toplam Premium
            </Typography>
            <Typography variant="h4">
              {stats.total_premium}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Aktif Premium
            </Typography>
            <Typography variant="h4" color="success.main">
              {stats.active_premium}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              İptal Edilenler
            </Typography>
            <Typography variant="h4" color="warning.main">
              {stats.cancelled_premium}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Aylık Gelir
            </Typography>
            <Typography variant="h4" color="primary.main">
              {formatCurrency(stats.monthly_revenue)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Premium Subscriptions Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Kullanıcı</TableCell>
                <TableCell>Paket</TableCell>
                <TableCell>Fiyat</TableCell>
                <TableCell>Başlangıç</TableCell>
                <TableCell>Bitiş</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Ödeme</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {premiumSubscriptions.map((premium) => (
                <TableRow key={premium.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={premium.user?.photos?.[0]}
                        sx={{ width: 40, height: 40 }}
                      >
                        {premium.user?.name?.charAt(0) || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {premium.user?.name || 'Bilinmeyen'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {premium.user?.email || premium.user_id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={getPackageDisplayName(premium.package_type)}
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(premium.price)}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(premium.start_date)}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(premium.end_date)}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={premium.status.toUpperCase()}
                      color={getStatusColor(premium.status)}
                      size="small"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {premium.payment_method}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    {premium.is_active && premium.status === 'active' && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleCancelPremium(premium.id, premium.user?.name || 'Bilinmeyen')}
                      >
                        İptal Et
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Premium Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Premium Abonelik Ver</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Kullanıcı ID"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              placeholder="Kullanıcının ID'sini girin"
              helperText="Kullanıcı detay sayfasından ID'yi kopyalayabilirsiniz"
            />

            <FormControl fullWidth>
              <InputLabel>Paket Tipi</InputLabel>
              <Select
                value={packageType}
                label="Paket Tipi"
                onChange={(e) => setPackageType(e.target.value)}
              >
                <MenuItem value="1_month">1 Aylık (₺89.99)</MenuItem>
                <MenuItem value="3_month">3 Aylık (₺199.99)</MenuItem>
                <MenuItem value="6_month">6 Aylık (₺399.99)</MenuItem>
                <MenuItem value="12_month">12 Aylık (₺599.99)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Admin Notu"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              multiline
              rows={3}
              placeholder="Bu premium aboneliğin verilme sebebini yazın..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            İptal
          </Button>
          <Button onClick={handleCreatePremium} variant="contained">
            Premium Ver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PremiumSubscriptionsPage; 